import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  ApiClientView, ClientSecretView, ErpColumnView, ErpFieldView, ErpProfileView, ErpRunView,
  ESignEnvelopeView, ESignProviderView, EventTypeView, ScimSetupView, SsoConfigurationView,
  WebhookDeliveryView, WebhookSecretView, WebhookSubscriptionView,
} from '../types'

const BASE = '/v1/integrations'

export function useApiClients() {
  return useQuery({
    queryKey: ['integrations', 'clients'],
    queryFn: async () => (await apiClient.get<ApiClientView[]>(`${BASE}/clients`)).data,
  })
}

/** The permissions a client may be given — reads only, which the backend also enforces. */
export function useOfferableScopes() {
  return useQuery({
    queryKey: ['integrations', 'scopes'],
    queryFn: async () => (await apiClient.get<string[]>(`${BASE}/scopes`)).data,
    staleTime: 5 * 60_000,
  })
}

export function useEventTypes() {
  return useQuery({
    queryKey: ['integrations', 'events'],
    queryFn: async () => (await apiClient.get<EventTypeView[]>(`${BASE}/events`)).data,
    staleTime: 5 * 60_000,
  })
}

export function useWebhooks() {
  return useQuery({
    queryKey: ['integrations', 'webhooks'],
    queryFn: async () =>
      (await apiClient.get<WebhookSubscriptionView[]>(`${BASE}/webhooks`)).data,
  })
}

export function useDeliveries(subscriptionId?: string) {
  return useQuery({
    queryKey: ['integrations', 'deliveries', subscriptionId ?? 'all'],
    queryFn: async () =>
      (await apiClient.get<WebhookDeliveryView[]>(`${BASE}/webhooks/deliveries`, {
        params: { subscriptionId: subscriptionId || undefined, limit: 100 },
      })).data,
  })
}

export function useSignatureRecipe() {
  return useQuery({
    queryKey: ['integrations', 'signature-recipe'],
    queryFn: async () =>
      (await apiClient.get<string>(`${BASE}/webhooks/signature-recipe`)).data,
    staleTime: Infinity,
  })
}

export function useMaxPageSize() {
  return useQuery({
    queryKey: ['integrations', 'max-page-size'],
    queryFn: async () => (await apiClient.get<number>(`${BASE}/max-page-size`)).data,
    staleTime: Infinity,
  })
}

export function useIntegrationMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['integrations'] })

  return {
    createClient: useMutation({
      mutationFn: async (body: {
        name: string; description?: string | null; scopes: string[]
        rateLimitPerMinute: number; expiresAt?: string | null
      }) => (await apiClient.post<ClientSecretView>(`${BASE}/clients`, body)).data,
      onSuccess: invalidate,
    }),
    rotateClient: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<ClientSecretView>(`${BASE}/clients/${id}/rotate`, {})).data,
      onSuccess: invalidate,
    }),
    setClientStatus: useMutation({
      mutationFn: async (body: { id: string; status: string; reason?: string | null }) =>
        (await apiClient.put<ApiClientView[]>(`${BASE}/clients/${body.id}/status`, {
          status: body.status, reason: body.reason ?? null,
        })).data,
      onSuccess: invalidate,
    }),
    setClientScopes: useMutation({
      mutationFn: async (body: { id: string; scopes: string[] }) =>
        (await apiClient.put<ApiClientView[]>(`${BASE}/clients/${body.id}/scopes`, {
          scopes: body.scopes,
        })).data,
      onSuccess: invalidate,
    }),
    subscribe: useMutation({
      mutationFn: async (body: { name: string; targetUrl: string; eventTypes: string[] }) =>
        (await apiClient.post<WebhookSecretView>(`${BASE}/webhooks`, body)).data,
      onSuccess: invalidate,
    }),
    updateWebhook: useMutation({
      mutationFn: async (body: {
        id: string; name: string; targetUrl: string; eventTypes: string[]
      }) => (await apiClient.put<WebhookSubscriptionView[]>(`${BASE}/webhooks/${body.id}`, {
        name: body.name, targetUrl: body.targetUrl, eventTypes: body.eventTypes,
      })).data,
      onSuccess: invalidate,
    }),
    setWebhookStatus: useMutation({
      mutationFn: async (body: { id: string; status: string }) =>
        (await apiClient.put<WebhookSubscriptionView[]>(`${BASE}/webhooks/${body.id}/status`, {
          status: body.status,
        })).data,
      onSuccess: invalidate,
    }),
    rotateWebhookSecret: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<WebhookSecretView>(`${BASE}/webhooks/${id}/rotate`, {})).data,
      onSuccess: invalidate,
    }),
    redeliver: useMutation({
      mutationFn: async (deliveryId: string) =>
        (await apiClient.post<WebhookDeliveryView>(
          `${BASE}/webhooks/deliveries/${deliveryId}/redeliver`, {})).data,
      onSuccess: invalidate,
    }),
  }
}

export function useSsoConfiguration() {
  return useQuery({
    queryKey: ['integrations', 'sso'],
    queryFn: async () => (await apiClient.get<SsoConfigurationView>(`${BASE}/sso`)).data,
  })
}

export function useScimSetup() {
  return useQuery({
    queryKey: ['integrations', 'scim'],
    queryFn: async () => (await apiClient.get<ScimSetupView>(`${BASE}/sso/scim`)).data,
    staleTime: 5 * 60_000,
  })
}

export function useSsoMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['integrations', 'sso'] })

  return {
    save: useMutation({
      mutationFn: async (body: {
        displayName: string; issuer: string; clientId: string; clientSecret?: string
        scopes?: string; emailClaim?: string; nameClaim?: string
        allowedEmailDomains?: string | null; jitProvisioning: boolean; enabled: boolean
      }) => (await apiClient.put<SsoConfigurationView>(`${BASE}/sso`, body)).data,
      onSuccess: invalidate,
    }),
    test: useMutation({
      mutationFn: async () => (await apiClient.post<string>(`${BASE}/sso/test`, {})).data,
      onSuccess: invalidate,
    }),
    setPasswordLogin: useMutation({
      mutationFn: async (allowed: boolean) =>
        (await apiClient.put<SsoConfigurationView>(`${BASE}/sso/password-login`, { allowed })).data,
      onSuccess: invalidate,
    }),
  }
}

// ------------------------------------------------------------------ ERP export

export function useErpFields() {
  return useQuery({
    queryKey: ['integrations', 'erp', 'fields'],
    queryFn: async () => (await apiClient.get<ErpFieldView[]>(`${BASE}/erp/fields`)).data,
    staleTime: Infinity,
  })
}

export function useErpProfiles() {
  return useQuery({
    queryKey: ['integrations', 'erp', 'profiles'],
    queryFn: async () => (await apiClient.get<ErpProfileView[]>(`${BASE}/erp/profiles`)).data,
  })
}

export function useErpRuns() {
  return useQuery({
    queryKey: ['integrations', 'erp', 'runs'],
    queryFn: async () => (await apiClient.get<ErpRunView[]>(`${BASE}/erp/runs`)).data,
  })
}

export function useErpMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['integrations', 'erp'] })

  return {
    save: useMutation({
      mutationFn: async (body: {
        id?: string; name: string; targetSystem?: string | null; delimiter: string
        dateFormat: string; decimalSeparator: string; includeHeader: boolean
        signedAmount: boolean; columnMapping: ErpColumnView[]; destination: string
        targetPath: string; sftpHost?: string | null; sftpPort?: number | null
        sftpUsername?: string | null; sftpPassword?: string | null; sftpHostKey?: string | null
        active: boolean
      }) => {
        const { id, ...rest } = body
        return id
          ? (await apiClient.put<ErpProfileView>(`${BASE}/erp/profiles/${id}`, rest)).data
          : (await apiClient.post<ErpProfileView>(`${BASE}/erp/profiles`, rest)).data
      },
      onSuccess: invalidate,
    }),
    preview: useMutation({
      mutationFn: async (body: { id: string; batchId: string }) =>
        (await apiClient.get<string[]>(`${BASE}/erp/profiles/${body.id}/preview`, {
          params: { batchId: body.batchId, rows: 10 },
        })).data,
    }),
    export: useMutation({
      mutationFn: async (body: { id: string; batchId: string }) =>
        (await apiClient.post<ErpRunView>(`${BASE}/erp/profiles/${body.id}/export`, null, {
          params: { batchId: body.batchId },
        })).data,
      onSuccess: invalidate,
    }),
  }
}

// --------------------------------------------------------------------- e-sign

const ESIGN = '/v1/esign'

export function useESignProviders() {
  return useQuery({
    queryKey: ['esign', 'providers'],
    queryFn: async () => (await apiClient.get<ESignProviderView[]>(`${ESIGN}/providers`)).data,
    staleTime: Infinity,
  })
}

export function useEnvelopes() {
  return useQuery({
    queryKey: ['esign', 'envelopes'],
    queryFn: async () => (await apiClient.get<ESignEnvelopeView[]>(`${ESIGN}/envelopes`)).data,
  })
}

export function useESignMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['esign'] })

  return {
    create: useMutation({
      mutationFn: async (body: {
        subject: string; message?: string | null; documentFileId?: string | null
        subjectType?: string | null; subjectId?: string | null; provider: string
        signers: { employeeId?: string | null; fullName: string; email: string; signingOrder: number }[]
      }) => (await apiClient.post<ESignEnvelopeView>(`${ESIGN}/envelopes`, body)).data,
      onSuccess: invalidate,
    }),
    send: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<ESignEnvelopeView>(`${ESIGN}/envelopes/${id}/send`, {})).data,
      onSuccess: invalidate,
    }),
    sign: useMutation({
      mutationFn: async (body: { id: string; signerId: string }) =>
        (await apiClient.post<ESignEnvelopeView>(`${ESIGN}/envelopes/${body.id}/sign`, {
          signerId: body.signerId,
        })).data,
      onSuccess: invalidate,
    }),
    decline: useMutation({
      mutationFn: async (body: { id: string; signerId: string; reason?: string }) =>
        (await apiClient.post<ESignEnvelopeView>(`${ESIGN}/envelopes/${body.id}/decline`, {
          signerId: body.signerId, reason: body.reason ?? null,
        })).data,
      onSuccess: invalidate,
    }),
    voidEnvelope: useMutation({
      mutationFn: async (body: { id: string; reason?: string }) =>
        (await apiClient.post<ESignEnvelopeView>(`${ESIGN}/envelopes/${body.id}/void`, {
          reason: body.reason ?? null,
        })).data,
      onSuccess: invalidate,
    }),
  }
}
